import { useState } from "react";
import axios from "axios";
import { Mail, Send, Phone, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { FadeUp } from "@/components/motion-ui";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactSection() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Merci de remplir le nom, l'email et le message ✨");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      toast.success(
        res.data.email_sent
          ? "Message envoyé ! Perle te répond très vite 💖"
          : "Message reçu ! On te recontacte rapidement 💖"
      );
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Oups, un souci. Réessaie ou écris à lagigafam@gmail.com");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative px-5 md:px-8 py-24 md:py-32 bg-neutral-950 text-white overflow-hidden"
    >
      <div className="absolute -top-32 -left-32 w-[500px] h-[500px] blob-pink pointer-events-none" />
      <div className="absolute -bottom-40 -right-32 w-[420px] h-[420px] blob-cyan pointer-events-none" />

      <div className="relative max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
        <FadeUp className="lg:col-span-5">
          <span className="inline-block text-xs font-bold uppercase tracking-[0.25em] text-pink-400 mb-3">
            ✦ Contact
          </span>
          <h2 className="font-display text-4xl md:text-6xl font-black uppercase tracking-tight leading-[0.95]">
            Une <span className="text-pink-500">question</span> ?
            <br />
            Écris-nous.
          </h2>
          <p className="mt-6 text-lg text-white/70 font-medium leading-relaxed">
            Conseil personnalisé, programme sur-mesure, partenariat ou simple
            envie de papoter — Perle et son équipe te répondent vite.
          </p>

          <div className="mt-10 space-y-5">
            <div
              data-testid="contact-info-email"
              className="flex items-center gap-4 bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-5 hover:border-pink-500/40 transition-colors"
            >
              <div className="h-12 w-12 rounded-2xl bg-pink-600 flex items-center justify-center">
                <Mail size={20} />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-white/50">
                  Email
                </p>
                <a
                  href="mailto:lagigafam@gmail.com"
                  className="font-display text-lg font-extrabold"
                >
                  lagigafam@gmail.com
                </a>
              </div>
            </div>

            <div className="flex items-center gap-4 bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-5">
              <div className="h-12 w-12 rounded-2xl bg-cyan-500 flex items-center justify-center">
                <MessageCircle size={20} />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-white/50">
                  Chatbot
                </p>
                <p className="font-display text-lg font-extrabold">
                  Discute avec notre IA — bas à droite 👉
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4 bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-5">
              <div className="h-12 w-12 rounded-2xl bg-amber-400 text-neutral-900 flex items-center justify-center">
                <Phone size={20} />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-white/50">
                  Formats
                </p>
                <p className="font-display text-lg font-extrabold">
                  Visio • Domicile • Salle
                </p>
              </div>
            </div>
          </div>
        </FadeUp>

        <FadeUp className="lg:col-span-7" delay={0.15}>
        <form
          onSubmit={submit}
          data-testid="contact-form"
          className="bg-white text-neutral-900 rounded-[2rem] p-7 md:p-10 shadow-2xl"
        >
          <h3 className="font-display text-2xl md:text-3xl font-black mb-6">
            Envoie ton message 💖
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-neutral-600">
                Nom *
              </label>
              <Input
                data-testid="contact-input-name"
                value={form.name}
                onChange={update("name")}
                placeholder="Ton prénom"
                className="mt-2 h-12 rounded-full border-pink-200 focus-visible:ring-pink-500 focus-visible:ring-2 px-5"
                required
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-neutral-600">
                Email *
              </label>
              <Input
                data-testid="contact-input-email"
                type="email"
                value={form.email}
                onChange={update("email")}
                placeholder="toi@email.com"
                className="mt-2 h-12 rounded-full border-pink-200 focus-visible:ring-pink-500 focus-visible:ring-2 px-5"
                required
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-neutral-600">
                Téléphone
              </label>
              <Input
                data-testid="contact-input-phone"
                value={form.phone}
                onChange={update("phone")}
                placeholder="06 ..."
                className="mt-2 h-12 rounded-full border-pink-200 focus-visible:ring-pink-500 focus-visible:ring-2 px-5"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-neutral-600">
                Sujet
              </label>
              <Input
                data-testid="contact-input-subject"
                value={form.subject}
                onChange={update("subject")}
                placeholder="BBL, drainage, infos..."
                className="mt-2 h-12 rounded-full border-pink-200 focus-visible:ring-pink-500 focus-visible:ring-2 px-5"
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="text-xs font-bold uppercase tracking-widest text-neutral-600">
              Message *
            </label>
            <Textarea
              data-testid="contact-input-message"
              value={form.message}
              onChange={update("message")}
              placeholder="Raconte-nous ton objectif, on s'occupe du reste ✨"
              rows={6}
              className="mt-2 rounded-3xl border-pink-200 focus-visible:ring-pink-500 focus-visible:ring-2 px-5 py-3"
              required
            />
          </div>

          <Button
            data-testid="contact-submit-button"
            type="submit"
            disabled={loading}
            className="mt-6 w-full h-14 rounded-full bg-pink-600 hover:bg-pink-700 text-white font-bold text-base shadow-[0_12px_40px_rgba(255,0,127,0.35)] hover:scale-[1.01] transition-transform"
          >
            {loading ? (
              "Envoi en cours..."
            ) : (
              <span className="flex items-center gap-2">
                Envoyer le message <Send size={16} />
              </span>
            )}
          </Button>
        </form>
        </FadeUp>
      </div>
    </section>
  );
}
